import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PATIENT_360_NAV_ITEMS } from './PatientSidebarNav';
import { usePatient360ModuleAvailability } from '@/modules/programs/hooks/usePatient360ModuleAvailability';

const ROUTE_MAP: Record<string, string> = {
  overview:        '',
  cases:           'cases',
  cycles:          'cycle',
  documents:       'documents',
  communications:  'communications',
  'call-logs':     'call-logs',
  'adverse-events':'ae-pc',
  activity:        'activity',
};

/**
 * Horizontal tab strip for Patient360 — mirrors WorkspaceNavTabs pattern.
 * Rendered by Layout.tsx below the global header on all /patients/:id/360 routes.
 */
export function Patient360NavTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { programId, patientId } = useParams<{ programId: string; patientId: string }>();

  const { modules } = usePatient360ModuleAvailability(programId, {
    enabled: Boolean(programId) && Boolean(patientId),
  });

  if (!programId || !patientId) return null;

  const base = `/programs/${programId}/patients/${patientId}/360`;

  const isActive = (key: string): boolean => {
    const sub = ROUTE_MAP[key];
    if (sub === '') return location.pathname === base || location.pathname === base + '/';
    return location.pathname.startsWith(`${base}/${sub}`);
  };

  const visibleItems = PATIENT_360_NAV_ITEMS.filter((item) => {
    if (item.key === 'adverse-events') {
      return !modules['ae-pc'].isLoading && modules['ae-pc'].isAvailable;
    }
    if (item.key === 'call-logs') {
      return !modules['call-logs'].isLoading && modules['call-logs'].isAvailable;
    }
    return true;
  });

  const handleClick = (key: string) => {
    const sub = ROUTE_MAP[key];
    navigate(sub ? `${base}/${sub}` : base);
  };

  return (
    <nav
      aria-label="Patient details navigation"
      className="flex-shrink-0 flex items-center justify-start lg:justify-center gap-0.5 border-b border-border bg-card overflow-x-auto scrollbar-hide px-4 py-1.5 lg:px-6"
    >
      {/* Parent nav — returns to Patient Workspace */}
      <button
        type="button"
        onClick={() => navigate(`/programs/${programId}/workspace`)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold whitespace-nowrap',
          'rounded-md transition-colors duration-150',
          'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <LayoutGrid className="h-3.5 w-3.5 flex-shrink-0" />
        Patient Workspace
      </button>

      {/* Divider between parent and child items */}
      <span className="mx-1 h-4 w-px bg-border/60 flex-shrink-0" aria-hidden />

      {visibleItems.map((item) => {
        const active = isActive(item.key);
        const Icon = item.icon;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => handleClick(item.key)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap',
              'rounded-md transition-colors duration-150',
              active
                ? 'bg-primary text-primary-foreground font-semibold'
                : 'font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5 flex-shrink-0" />
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}
